import { useState } from "react";
import { createPortal } from "react-dom";
import { apiRequest } from "../../../network/apiClient";
import { API_ENDPOINTS } from "../../../api/apiEndpoints";
import { toast } from "react-toastify";

interface AddReferrerButtonProps {
  refetch: () => void;
  trigger?: React.ReactNode;
}

interface FormData {
  name: string;
  lead_source: string;
  mobile_no: string;
  email: string;
}

const AddReferrerButton = ({ refetch, trigger }: AddReferrerButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    lead_source: "",
    mobile_no: "",
    email: "",
  });

  const leadSourceOptions: string[] = [
    "Referrer MM Employee",
    "Referrer Margin Sharing",
  ];

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.mobile_no || !formData.email || !formData.lead_source) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsLoading(true);
    try {
      await apiRequest(
        `${import.meta.env.VITE_BASE_URL}/${API_ENDPOINTS.SETTINGS.ADD_REFERRER}`,
        "POST",
        formData
      );
      if (refetch) refetch();
      toast.success("Referrer Successfully Added");
      setIsModalOpen(false);
      setFormData({ name: "", lead_source: "", mobile_no: "", email: "" });
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to add referrer");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Render custom trigger if provided, otherwise fallback to default button */}
      <div onClick={() => setIsModalOpen(true)} className="cursor-pointer">
        {trigger ?? (
          <button className="text-[#1E293B] block px-4 py-2 text-sm hover:bg-[#F1F5F9] text-left w-full">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="inline-block w-5 h-5 mr-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
            </svg>
            Add Referrer
          </button>
        )}
      </div>

      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50">
            <div className="bg-white p-6 rounded-t-2xl md:rounded-lg w-full md:w-1/3 max-h-[90vh] overflow-y-auto">
              <div className="border-b border-gray-300 mb-6">
                <h2 className="text-xl font-bold mb-2">Add Referrer</h2>
              </div>
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter referrer name"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Lead Source <span className="text-red-500">*</span>
                    </label>
                  <select
                    name="lead_source"
                    value={formData.lead_source}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                  >
                    <option value="" disabled>Select a lead source</option>
                    {leadSourceOptions.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="mobile_no"
                    value={formData.mobile_no}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Email <span className="text-red-500">*</span>
                    </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter email address"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-3 bg-white border-[1px] border-black rounded-3xl hover:bg-gray-100"
                  >
                    <b>Cancel</b>
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className={`rounded-3xl px-8 py-3 text-black ${
                      isLoading ? "bg-gray-500" : "bg-yellow-300 hover:bg-yellow-400"
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading ? "Adding..." : <b>Submit</b>}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AddReferrerButton;